import {
  createContext,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { Role, UserResponse } from '@clarity/contract';

/**
 * The in-memory + persisted session (Req 6.5). After a successful login the app
 * holds the signed JWT plus the (hash-less) user it was issued to. The role and
 * `clientId` used for UI gating come straight from that `user` object — we do
 * NOT decode or verify the JWT client-side (the server is the only authority on
 * what the token grants; the UI gate is convenience, Req 2.3/6.6).
 */
export interface Session {
  /** The signed JWT, attached as `Authorization: Bearer <token>` on every call. */
  token: string;
  /** The authenticated principal (no password hash — projected out server-side). */
  user: UserResponse;
}

const STORAGE_KEY = 'clarity.session';

/**
 * Read any persisted session from `localStorage`. Returns `null` when absent or
 * malformed — a corrupt entry is treated as "logged out" rather than throwing.
 */
export function loadSession(): Session | null {
  if (typeof localStorage === 'undefined') return null;
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (
      parsed &&
      typeof parsed === 'object' &&
      'token' in parsed &&
      'user' in parsed &&
      typeof (parsed as Session).token === 'string'
    ) {
      return parsed as Session;
    }
    return null;
  } catch {
    return null;
  }
}

function persistSession(session: Session | null): void {
  if (typeof localStorage === 'undefined') return;
  if (session) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  } else {
    localStorage.removeItem(STORAGE_KEY);
  }
}

/**
 * The reactive session surface the app consumes. `role`/`clientId` are lifted
 * out of `session.user` so route gating can read them directly.
 */
export interface SessionContextValue {
  session: Session | null;
  role: Role | null;
  clientId: string | null;
  isAuthenticated: boolean;
  signIn: (session: Session) => void;
  signOut: () => void;
}

const SessionContext = createContext<SessionContextValue | null>(null);

/**
 * A module-level accessor for the current token. The ts-rest client's
 * `baseHeaders` reads this synchronously when building a request, so the
 * `Authorization` header always reflects the live session without rebuilding
 * the client. Kept in sync by `SessionProvider`.
 */
let currentToken: string | null = loadSession()?.token ?? null;

/** Read the live JWT (used by the ts-rest client's auth header). */
export function getAuthToken(): string | null {
  return currentToken;
}

export interface SessionProviderProps {
  children: ReactNode;
  /** Seed the session (tests inject a known session; app defaults to storage). */
  initialSession?: Session | null | undefined;
}

/**
 * Provides the session to the tree and keeps `localStorage` + the module-level
 * token accessor in sync. Login mutations call `signIn`; `signOut` clears
 * everything and drops the UI back to the login view.
 */
export function SessionProvider({ children, initialSession }: SessionProviderProps) {
  const [session, setSession] = useState<Session | null>(
    initialSession !== undefined ? initialSession : loadSession(),
  );
  currentToken = session?.token ?? null;

  const value = useMemo<SessionContextValue>(() => {
    return {
      session,
      role: session?.user.role ?? null,
      clientId: session?.user.clientId ?? null,
      isAuthenticated: session !== null,
      signIn: (next: Session) => {
        currentToken = next.token;
        persistSession(next);
        setSession(next);
      },
      signOut: () => {
        currentToken = null;
        persistSession(null);
        setSession(null);
      },
    };
  }, [session]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

/** Access the session surface. Throws if used outside `SessionProvider`. */
export function useSession(): SessionContextValue {
  const ctx = useContext(SessionContext);
  if (!ctx) {
    throw new Error('useSession must be used within a SessionProvider');
  }
  return ctx;
}
